import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/RCS-Logo-White.png";
import CollapsedLogo from "../assets/RCS-Logo-White.png";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/auth-slice";
import { confirmAlert } from "react-confirm-alert";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { authFetch, API_BASE_URL } from "../config/api";
// test
const AccountingSidebar = ({ forceExpanded = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const isLiquidationApprover = currentUserId === 127;
  const isCostAnalysisInitialApprover = currentUserId === 130;
  const isCostAnalysisFinalApprover = currentUserId === 127;
  const normalizedRole = String(user?.role || "").trim().toLowerCase();
  const isSuperadmin = normalizedRole === "superadmin";
  const isAccountingDept = user?.department?.toLowerCase() === "accounting";
  const isSuperadminAccounting = isSuperadmin && isAccountingDept;
  const canViewReports = isSuperadmin || isAccountingDept;
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [textVisible, setTextVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      const width = window.innerWidth;
      const isLarge = Boolean(forceExpanded || width >= 1025);
      setIsLargeScreen(isLarge);
      setTextVisible(isLarge);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [forceExpanded]);

  useEffect(() => {
    const fetchPendingRequestCount = async () => {
      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/accounting/requests`,
          {
            headers: {
              "Cache-Control":
                "no-store, no-cache, must-revalidate, proxy-revalidate",
              Pragma: "no-cache",
              Expires: "0",
            },
          }
        );
        const json = await response.json();
        const rows = Array.isArray(json?.data) ? json.data : [];
        const pendingCount = rows.filter((row) =>
          ["pending", "partially_approved", "submitted"].includes(
            String(row.status || "").toLowerCase()
          )
        ).length;
        setPendingRequestCount(pendingCount);
      } catch (error) {
        console.error("Error fetching pending request count:", error);
      }
    };

    fetchPendingRequestCount();
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    dispatch(logoutUser()).then((data) => {
      const message = data?.payload?.message;
      if (data?.payload?.success) {
        toast.success(message, { position: "top-right" });
      } else {
        toast.error(message, { position: "top-right" });
      }
      sessionStorage.removeItem("passwordResetNotificationClosed");
    });
  };

  const handleLogoutConfirmation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-sign-out-alt text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">
                Confirm Logout
              </h2>
              <p className="text-grey">
                Are you sure you want to sign out of your account?
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };

  const [openDirectory, setOpenDirectory] = useState(true);
  const [openRequest, setOpenRequest] = useState(true);
  const [openLiquidation, setOpenLiquidation] = useState(true);
  const [openCostAnalysis, setOpenCostAnalysis] = useState(true);
  const [openReports, setOpenReports] = useState(true);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [pendingFinalCostAnalysisCount, setPendingFinalCostAnalysisCount] = useState(0);

  useEffect(() => {
    if (!isCostAnalysisFinalApprover) {
      setPendingFinalCostAnalysisCount(0);
      return undefined;
    }

    let cancelled = false;
    const fetchPendingCostAnalysisCount = async () => {
      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/accounting/cost-analysis?scope=final_approver&summary=1`,
          { credentials: "include" }
        );
        const json = await response.json();
        if (!cancelled && json.success) {
          const pendingCount = (json.data || []).filter((row) =>
            ["submitted", "partially_approved", "verified"].includes(
              String(row.cost_analysis_stage || "").toLowerCase()
            )
          ).length;
          setPendingFinalCostAnalysisCount(pendingCount);
        }
      } catch (error) {
        if (!cancelled) console.error("Error fetching pending cost analysis count:", error);
      }
    };

    fetchPendingCostAnalysisCount();
    return () => {
      cancelled = true;
    };
  }, [isCostAnalysisFinalApprover]);

  const navItems = [
    {
      to: "/accounting",
      label: "Accounting Home",
      icon: "fa-chart-pie",
      level: 0,
    },
    {
      to: "/accounting/requests/create",
      label: "Request Creation",
      icon: "fa-file-circle-plus",
      level: 2,
      group: "Request",
    },
    {
      to: "/accounting/requests",
      label: "Request Summary",
      icon: "fa-table-list",
      level: 2,
      group: "Request",
    },
    {
      to: "/accounting/check-vouchers",
      label: "Voucher List",
      icon: "fa-file-invoice",
      level: 2,
      group: "Request",
    },
    {
      to: "/accounting/liquidation",
      label: "Liquidation Workflow",
      icon: "fa-receipt",
      level: 2,
      group: "Liquidation",
    },
    // {
    //   to: "/accounting/liquidation-user127-approvals",
    //   label: "Liquidation Approval",
    //   icon: "fa-circle-check",
    //   level: 2,
    //   group: "Liquidation",
    //   onlyForLiquidationApprover: true,
    // },
    {
      to: "/accounting/liquidation-approvals",
      label: "Leader Liquidation Approval",
      icon: "fa-user-check",
      level: 2,
      group: "Liquidation",
    },
    {
      to: "/accounting/cost-analysis",
      label: "Cost Analysis Workflow",
      icon: "fa-calculator",
      level: 2,
      group: "Cost Analysis",
    },
    {
      to: "/accounting/cost-analysis-templates",
      label: "CA Templates",
      icon: "fa-file-lines",
      level: 2,
      group: "Cost Analysis",
    },
    {
      to: "/accounting/cost-analysis-initial-approvals",
      label: "Cost Analysis Initial Approval",
      icon: "fa-user-check",
      level: 2,
      group: "Cost Analysis",
      onlyForCostAnalysisInitialApprover: true,
    },
    {
      to: "/accounting/cost-analysis-user127-approvals",
      label: "Cost Analysis Final Approval",
      icon: "fa-circle-check",
      level: 2,
      group: "Cost Analysis",
      onlyForCostAnalysisFinalApprover: true,
    },
    {
      to: "/accounting/jo-ledger",
      label: "JO Ledger",
      icon: "fa-book-open",
      level: 0,
      standalone: true,
    },
    {
      to: "/accounting/vendors",
      label: "Vendor Enlistment",
      icon: "fa-building",
      level: 1,
    },
    {
      to: "/accounting/customers",
      label: "Customer Enlistment",
      icon: "fa-user-group",
      level: 1,
    },
    {
      to: "/accounting/particulars",
      label: "Particulars",
      icon: "fa-list",
      level: 1,
    },
    {
      to: "/accounting/chart-accounts",
      label: "Chart of Accounts",
      icon: "fa-book",
      level: 1,
    },
    {
      to: "/accounting/production-report",
      label: "Production Report",
      icon: "fa-file-lines",
      level: 2,
      group: "Reports",
    },
  ];
  if (isSuperadminAccounting) {
    navItems.splice(3, 0, {
      to: "/accounting/check-vouchers/pending",
      label: "Pending Vouchers",
      icon: "fa-file-circle-check",
      level: 2,
      group: "Request",
    });
  }

  return (
    <div className="flex font-poppins h-full">
      <div
        className={clsx(
          "h-full bg-blue text-white transition-all duration-500 ease-out backdrop-blur-xl shadow-2xl border-r border-white/10 relative",
          "w-full"
        )}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-16 right-4 w-32 h-32 bg-yellow/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-28 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        <div
          className={`relative flex items-center justify-center p-6 border-b border-white/10 backdrop-blur-sm ${
            isLargeScreen ? "h-28" : "h-20"
          }`}
        >
          <button
            type="button"
            onClick={() => navigate("/superAdmin/super-dashboard")}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center shadow border border-white/10"
            aria-label="Go back"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <img
            src={isLargeScreen ? Logo : CollapsedLogo}
            alt="Logo"
            className={`transition-all duration-500 ${
              isLargeScreen
                ? "w-44 h-16 object-contain"
                : "w-12 h-12 object-contain"
            }`}
          />
        </div>

        <nav
          className="mt-8 px-4 space-y-3 overflow-y-auto pb-28"
          style={{ maxHeight: "calc(100vh - 16rem)" }}
        >
          <div className="px-4 py-2 text-xs uppercase tracking-wide text-white/60">
            Accounting
          </div>
          <Link
            to="/accounting"
            className={clsx(
              "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
              isLargeScreen ? "justify-start" : "justify-center",
              isActive("/accounting")
                ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            )}
          >
            <div
              className={clsx(
                "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                isActive("/accounting")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              )}
            >
              <i className="fa-solid fa-chart-pie text-lg"></i>
              {isActive("/accounting") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>
            {textVisible && (
              <span
                className={clsx(
                  "font-semibold transition-all duration-300",
                  isActive("/accounting")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                )}
              >
                Accounting Home
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setOpenDirectory((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
          >
            <i
              className={`fa-solid fa-chevron-${
                openDirectory ? "down" : "right"
              } text-[10px]`}
            ></i>
            {textVisible && <span>Directory</span>}
          </button>

          {openDirectory &&
            navItems
              .filter((n) => n.level === 1)
              .map((item, idx) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={clsx(
                    "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
                    isLargeScreen ? "justify-start" : "justify-center",
                    "pl-6",
                    isActive(item.to)
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  )}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div
                    className={clsx(
                      "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                      isActive(item.to)
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    )}
                  >
                    <i className={`fa-solid ${item.icon} text-lg`}></i>
                    {isActive(item.to) && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "font-semibold transition-all duration-300",
                          isActive(item.to)
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        )}
                      >
                        {item.label}
                      </span>
                    </div>
                  )}
                </Link>
              ))}

          <button
            type="button"
            onClick={() => setOpenRequest((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
          >
            <i
              className={`fa-solid fa-chevron-${
                openRequest ? "down" : "right"
              } text-[10px]`}
            ></i>
            {textVisible && <span>Request</span>}
          </button>

          {openRequest &&
            navItems
              .filter((n) => n.group === "Request")
              .map((item, idx) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={clsx(
                    "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
                    isLargeScreen ? "justify-start" : "justify-center",
                    "pl-6",
                    isActive(item.to)
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  )}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div
                    className={clsx(
                      "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                      isActive(item.to)
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    )}
                  >
                    <i className={`fa-solid ${item.icon} text-lg`}></i>
                    {isActive(item.to) && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "font-semibold transition-all duration-300",
                          isActive(item.to)
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        )}
                      >
                        {item.label}
                      </span>
                      {item.to === "/accounting/requests" &&
                        pendingRequestCount > 0 && (
                          <span
                            className={clsx(
                              "inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold",
                              isActive(item.to)
                                ? "bg-blue text-white"
                                : "bg-yellow text-blue"
                            )}
                            title={`${pendingRequestCount} request${
                              pendingRequestCount === 1 ? "" : "s"
                            } pending approval`}
                          >
                            {pendingRequestCount}
                          </span>
                        )}
                    </div>
                  )}
                </Link>
              ))}

          <button
            type="button"
            onClick={() => setOpenLiquidation((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
          >
            <i
              className={`fa-solid fa-chevron-${
                openLiquidation ? "down" : "right"
              } text-[10px]`}
            ></i>
            {textVisible && <span>Liquidation</span>}
          </button>

          {openLiquidation &&
            navItems
              .filter((n) => n.group === "Liquidation")
              .filter(
                (n) => !n.onlyForLiquidationApprover || isLiquidationApprover
              )
              .map((item, idx) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={clsx(
                    "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
                    isLargeScreen ? "justify-start" : "justify-center",
                    "pl-6",
                    isActive(item.to)
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  )}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div
                    className={clsx(
                      "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                      isActive(item.to)
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    )}
                  >
                    <i className={`fa-solid ${item.icon} text-lg`}></i>
                    {isActive(item.to) && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <span
                      className={clsx(
                        "font-semibold transition-all duration-300",
                        isActive(item.to)
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      )}
                    >
                      {item.label}
                    </span>
                  )}
                </Link>
              ))}

          <button
            type="button"
            onClick={() => setOpenCostAnalysis((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
          >
            <i
              className={`fa-solid fa-chevron-${
                openCostAnalysis ? "down" : "right"
              } text-[10px]`}
            ></i>
            {textVisible && <span>Cost Analysis</span>}
          </button>

          {openCostAnalysis &&
            navItems
              .filter((n) => n.group === "Cost Analysis")
              .filter(
                (n) =>
                  (!n.onlyForCostAnalysisFinalApprover || isCostAnalysisFinalApprover) &&
                  (!n.onlyForCostAnalysisInitialApprover || isCostAnalysisInitialApprover)
              )
              .map((item, idx) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={clsx(
                    "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
                    isLargeScreen ? "justify-start" : "justify-center",
                    "pl-6",
                    isActive(item.to)
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  )}
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div
                    className={clsx(
                      "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                      isActive(item.to)
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    )}
                  >
                    <i className={`fa-solid ${item.icon} text-lg`}></i>
                    {isActive(item.to) && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "font-semibold transition-all duration-300",
                          isActive(item.to)
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        )}
                      >
                        {item.label}
                      </span>
                      {item.to === "/accounting/cost-analysis-user127-approvals" &&
                        pendingFinalCostAnalysisCount > 0 && (
                          <span
                            className={clsx(
                              "inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold",
                              isActive(item.to)
                                ? "bg-blue text-white"
                                : "bg-yellow text-blue"
                            )}
                            title={`${pendingFinalCostAnalysisCount} cost analysis report${
                              pendingFinalCostAnalysisCount === 1 ? "" : "s"
                            } pending final approval`}
                          >
                            {pendingFinalCostAnalysisCount}
                          </span>
                        )}
                    </div>
                  )}
                  </Link>
              ))}

          {/* JO Ledger - Standalone item below Cost Analysis */}
          <Link
            to="/accounting/jo-ledger"
            className={clsx(
              "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
              isLargeScreen ? "justify-start" : "justify-center",
              isActive("/accounting/jo-ledger") ||
                location.pathname.startsWith("/accounting/jo-ledger")
                ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            )}
          >
            <div
              className={clsx(
                "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                isActive("/accounting/jo-ledger") ||
                  location.pathname.startsWith("/accounting/jo-ledger")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              )}
            >
              <i className="fa-solid fa-book-open text-lg"></i>
              {(isActive("/accounting/jo-ledger") ||
                location.pathname.startsWith("/accounting/jo-ledger")) && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>
            {textVisible && (
              <span
                className={clsx(
                  "font-semibold transition-all duration-300",
                  isActive("/accounting/jo-ledger") ||
                    location.pathname.startsWith("/accounting/jo-ledger")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                )}
              >
                JO Ledger
              </span>
            )}
          </Link>

          {/* Reports Section - Accounting department or Superadmin */}
          {canViewReports && (
            <>
              <button
                type="button"
                onClick={() => setOpenReports((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
              >
                <i
                  className={`fa-solid fa-chevron-${
                    openReports ? "down" : "right"
                  } text-[10px]`}
                ></i>
                {textVisible && <span>Reports</span>}
              </button>

              {openReports &&
                navItems
                  .filter((n) => n.group === "Reports")
                  .map((item, idx) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={clsx(
                        "group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.01]",
                        isLargeScreen ? "justify-start" : "justify-center",
                        "pl-6",
                        isActive(item.to)
                          ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                          : "text-white hover:bg-white/10 hover:shadow-lg"
                      )}
                      style={{ animationDelay: `${idx * 80}ms` }}
                    >
                      <div
                        className={clsx(
                          "relative flex items-center justify-center w-6 h-6 transition-all duration-300",
                          isActive(item.to)
                            ? "text-blue"
                            : "text-white group-hover:text-yellow"
                        )}
                      >
                        <i className={`fa-solid ${item.icon} text-lg`}></i>
                        {isActive(item.to) && (
                          <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                        )}
                      </div>
                      {textVisible && (
                        <span
                          className={clsx(
                            "font-semibold transition-all duration-300",
                            isActive(item.to)
                              ? "text-blue"
                              : "text-white group-hover:text-yellow"
                          )}
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  ))}
            </>
          )}
        </nav>

        <div className="absolute bottom-0 w-full px-4 py-5 border-t border-white/10 backdrop-blur-sm">
          <Link
            to="/accounting/profile"
            className={`flex items-center gap-3 ${
              textVisible ? "justify-start" : "justify-center"
            } hover:bg-white/10 rounded-2xl px-2 py-1 transition-colors`}
          >
            <div className="relative">
              <div className="bg-yellow text-blue font-bold rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg">
                {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`}
              </div>
              <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
            </div>
            {textVisible && (
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white text-sm truncate">
                  {`${user?.first_name || ""} ${
                    user?.last_name || ""
                  }`.trim() || user?.user_name}
                </p>
                <p className="text-xs text-white/70 truncate">View Profile</p>
              </div>
            )}
          </Link>
          <div className="mt-3">
            <button
              onClick={handleLogoutConfirmation}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red hover:bg-red/90 text-white transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <i className="fa-solid fa-right-from-bracket"></i>
              {textVisible && <span className="font-semibold">Logout</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingSidebar;
